<template>
  <div>
    <BackgroundSVG />

    <div id="hero" class="relative overflow-hidden bg-bush-900/40 text-center shadow-2xl">
      <ClientOnly>
        <HeroScene />
      </ClientOnly>

      <div class="relative py-28 sm:py-36 px-6 flex items-center flex-col">
        <p class="text-sm font-semibold uppercase tracking-[0.3em] text-golden-grass-400">
          Statera &mdash; by Collor
        </p>
        <h1 class="mt-4 max-w-3xl text-5xl sm:text-6xl font-medium text-marshland-50 font-(family-name:--font-logo)">
          Remote dispensing that <span class="text-golden-grass-400">runs itself</span>
        </h1>
        <p class="mt-6 max-w-2xl text-lg sm:text-xl text-marshland-200">
          Solar-powered, cloud-connected dispensers for veterinary practices.
          They work at remote sites for months &mdash; no daily visits, no guesswork.
        </p>
        <div class="mt-10 flex flex-col sm:flex-row items-center gap-4">
          <CTAButton href="#contact" icon="i-lucide-arrow-right">
            Become a partner practice
          </CTAButton>
          <CTAButton href="#current-deployment" variant="secondary" icon="i-lucide-map-pin">
            See it in the field
          </CTAButton>
        </div>
        <a
            href="https://www.vrt.be/vrtnws/nl/kijk/2026/06/02/-diervriendelijker-en-efficienter-gentse-duiven-krijgen-antico/"
            target="_blank"
            rel="noopener"
            class="mt-8 inline-flex items-center gap-2 text-sm text-marshland-300 transition-colors hover:text-golden-grass-200"
        >
          <UIcon name="i-lucide-tv" class="w-4 h-4 text-golden-grass-400" />
          As featured on VRT NWS &middot; Terzake
          <UIcon name="i-lucide-external-link" class="w-3.5 h-3.5" />
        </a>
      </div>

      <div class="relative h-px bg-gradient-to-r from-transparent via-golden-grass/60 to-transparent"></div>
    </div>

    <UPageSection id="what-is-statera">
      <div class="border border-white/5 rounded-2xl backdrop-blur-lg shadow-2xl px-8 py-15">
        <SectionTitle
            eyebrow="The platform"
            title="Statera"
            description="Built for real conditions"
        />

        <p class="text-lg mb-6">
          Statera lets veterinary practices run automated dispensing at remote client sites without daily visits. We
          provide the hardware, connectivity, and management software as one service. You set treatments and watch the
          system. We keep it running.
        </p>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <FeatureCard
              icon="i-lucide-sunrise"
              title="Automated Sunrise Dispensing"
              description="Each dispenser wakes with the sun, performs the scheduled dose, and then powers down to save energy."
          />
          <FeatureCard
              icon="i-lucide-sliders-horizontal"
              title="Remote Configuration"
              description="Change dosing and timing from your dashboard. Devices apply updates automatically at the next wake cycle."
          />
          <FeatureCard
              icon="i-lucide-activity"
              title="Monitoring & Alerts"
              description="View battery status, fill levels, and site health. Get notified when a location needs attention."
          />
        </div>
      </div>
    </UPageSection>

    <UPageSection id="current-deployment">
      <div class="border border-white/5 rounded-2xl backdrop-blur-lg shadow-2xl px-8 py-15">
        <SectionTitle
            eyebrow="Current deployment"
            title="Proven in the field"
            description="In daily use for municipal pigeon-control programs across Belgium"
        />

        <p class="text-lg mb-6">
          Statera is in daily use at
          <a href="https://www.neorni.be" target="_blank" class="font-medium text-golden-grass-300 hover:text-golden-grass-200 hover:underline">Neorni</a>,
          a veterinary practice focused on humane pigeon population control. Through their initiative
          <a href="https://www.vetsforcitypigeons.com" target="_blank" class="font-medium text-golden-grass-300 hover:text-golden-grass-200 hover:underline">
            Vets For City Pigeons</a>, they manage contraceptive programs for cities across Belgium.
        </p>

        <p class="text-lg mb-6">
          Neorni had already automated their dispensers and proven the concept worked. But as their network expanded,
          they ran into a predictable scaling challenge: they couldn&rsquo;t see what was happening across all their sites
          without physically visiting each one. Which units were active? Which were running low? Which had gone offline?
          Managing a growing network without remote insight wasn&rsquo;t sustainable.
        </p>

        <p class="text-lg mb-6">
          Collor solved that visibility gap. By connecting every dispenser to the Statera platform, Neorni now gets live
          data, remote management, and daily status checks all from one place.
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FeatureCard
              icon="i-lucide-activity"
              title="Operational Status"
              description="Field-proven hardware and connectivity allow dispenser units to run autonomously for months. Each dispenser maintains stable uptime in outdoor conditions and issues clear alerts when intervention is needed, supporting reliable long-term municipal deployment."
          />

          <FeatureCard
              icon="i-lucide-badge-check"
              title="Validated Business Model"
              description="Cities adopt a predictable, service-oriented model through Vets For City Pigeons. Neorni provides field operations, Collor delivers the connected infrastructure, and the approach scales smoothly from one municipality to the next."
          />
        </div>

        <div
            class="mt-12 relative overflow-hidden rounded-2xl border border-golden-grass/25
         bg-gradient-to-r from-black/40 via-black/30 to-transparent
         px-8 py-10 shadow-2xl backdrop-blur-xl"
        >
          <!-- soft glow accents -->
          <div class="pointer-events-none absolute inset-0 opacity-50">
            <div class="absolute -left-10 top-0 h-40 w-40 rounded-full bg-golden-grass/20 blur-3xl"></div>
            <div class="absolute right-0 -bottom-10 h-48 w-48 rounded-full bg-golden-grass/10 blur-3xl"></div>
          </div>

          <div
              class="relative flex flex-col items-center gap-6 text-center md:flex-row md:items-center md:justify-between md:text-left">
            <div>
              <p class="text-sm font-semibold uppercase tracking-[0.25em] text-golden-grass-400">
                In the news
              </p>
              <h2 class="mt-2 text-3xl font-semibold text-marshland-50">
                National coverage on VRT's Terzake
              </h2>
              <p class="mt-3 text-base max-w-2xl text-marshland-200/90">
                Belgian national broadcaster VRT NWS reported on the pigeon contraception programme in Ghent:
                <span class="italic">&ldquo;Diervriendelijker en efficiënter&rdquo;: Gentse duiven krijgen
                anticonceptiepil</span> &mdash; the approach Statera-connected dispensers support in the field
                (Terzake, 2 June 2026).
              </p>
            </div>

            <div class="md:flex-shrink-0">
              <CTAButton
                  href="https://www.vrt.be/vrtnws/nl/kijk/2026/06/02/-diervriendelijker-en-efficienter-gentse-duiven-krijgen-antico/"
                  target="_blank"
                  variant="secondary"
                  icon="i-lucide-play"
              >
                Watch the report
              </CTAButton>
            </div>
          </div>
        </div>

        <div
            class="mt-6 relative overflow-hidden rounded-2xl border border-golden-grass/25
         bg-gradient-to-r from-black/40 via-black/30 to-transparent
         px-8 py-10 shadow-2xl backdrop-blur-xl"
        >
          <!-- soft glow accents -->
          <div class="pointer-events-none absolute inset-0 opacity-50">
            <div class="absolute -left-10 top-0 h-40 w-40 rounded-full bg-golden-grass/20 blur-3xl"></div>
            <div class="absolute right-0 -bottom-10 h-48 w-48 rounded-full bg-golden-grass/10 blur-3xl"></div>
          </div>

          <div
              class="relative flex flex-col items-center gap-6 text-center md:flex-row md:items-center md:justify-between md:text-left">
            <div>
              <p class="text-sm font-semibold uppercase tracking-[0.25em] text-golden-grass-400">
                See it live
              </p>
              <h2 class="mt-2 text-3xl font-semibold text-marshland-50">
                Statera in daily use at Neorni
              </h2>
              <p class="mt-3 text-base max-w-2xl text-marshland-200/90">
                Visit
                <a
                    href="https://www.vetsforcitypigeons.com"
                    target="_blank"
                    class="font-semibold text-golden-grass-300 hover:text-golden-grass-200 hover:underline"
                >VetsForCityPigeons.com</a> to see how Neorni turned a proven concept into a connected, scalable
                municipal service using Statera.
              </p>
            </div>

            <div class="md:flex-shrink-0">
              <CTAButton
                  href="https://www.vetsforcitypigeons.com"
                  target="_blank"
                  variant="secondary"
                  icon="i-lucide-external-link"
              >
                Visit VetsForCityPigeons.com
              </CTAButton>
            </div>
          </div>
        </div>
      </div>

    </UPageSection>

    <UPageSection id="how-it-works">
      <div class="border border-white/5 rounded-2xl backdrop-blur-lg shadow-2xl px-8 py-15">
        <SectionTitle
            eyebrow="How it works"
            title="Remote. Monitor. Control."
            description="Autonomous in the field, fully managed from your desk"
        />

        <p class="text-lg pb-15">
          Each dispenser wakes with the sun, dispenses based on the latest instructions, sends a quick report, and
          sleeps again to save power. You can change doses remotely from your desk or phone. The system keeps you
          informed and working, even when the connection drops.
        </p>

        <div id="remote" class="pb-20">
          <SectionHeader
              title="Remote"
              description="Dispensers that operate on their own, day after day."
          />

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
                icon="i-lucide-sunrise"
                title="Sunrise Cycle"
                description="Dispensers wake with the sun, handle the dispense procedure, then conserve power until the next cycle."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-sun"
                title="Solar Powered"
                description="Powered by solar panels and a large battery so it can run for months without anyone visiting the site."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-wifi"
                title="Cloud Connected"
                description="Each morning it sends telemetry data and receives new instructions. No manual syncing needed."
            />
            <FeatureCard
                icon="i-lucide-compass"
                title="Autonomous Operation"
                description="If the network goes down, the dispenser still follows its schedule and keeps working."
            />
          </div>
        </div>

        <div id="monitor" class="pb-20">
          <SectionHeader
              title="Monitor"
              description="Clear insight into every dispenser so you always know what is happening."
          />

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
                icon="i-lucide-beaker"
                title="Fill Level Tracking"
                description="See which locations are running low. Plan refills proactively instead of reactively."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-layout-dashboard"
                title="Operations Dashboard"
                description="A quick overview of all locations, sorted by priority, with full history for each site."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-activity"
                title="Health Diagnostics"
                description="Track battery, motor, and connectivity. The system flags early signs of trouble so you can act before dosing is interrupted."
            />
            <FeatureCard
                icon="i-lucide-mail"
                title="Email Alerts"
                description="Get a daily status report in your inbox with an overview of all dispensers and any issues that need attention."
            />
          </div>
        </div>

        <div id="control">
          <SectionHeader
              title="Control"
              description="Define timing and dosing from your practice or on the road."
          />

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
                icon="i-lucide-map-pin"
                title="Location Management"
                description="Manage client sites, assign dispensers, and track their status in one place."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-target"
                title="Precise Dosing"
                description="Change the dose size remotely. No field visit needed for small adjustments."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-clock"
                title="Adaptive Scheduling"
                description="Follow the sunrise or use fixed times. The system adjusts automatically through the seasons."
            />
            <FeatureCard
                icon="i-lucide-monitor-smartphone"
                title="Access Anywhere"
                description="Use the dashboard on desktop, tablet, or phone. Full control, wherever you are."
            />
          </div>
        </div>

        <div v-if="false" class="mt-10">
          <p class="text-lg">
            Every dispense event is logged with timestamp, dosage, and telemetry for full traceability. Records are
            stored for up to five years to support compliance and transparency. Veterinary practices remain
            responsible for official medication records and reporting.
          </p>
        </div>
      </div>
    </UPageSection>

    <UPageSection id="development-status">
      <div class="border border-white/5 rounded-2xl backdrop-blur-lg shadow-2xl px-8 py-15">
        <SectionTitle
            eyebrow="Development status"
            title="From validated concept to mature platform"
            description="The foundation is proven. Now we are hardening it for scale."
        />

        <p class="text-lg mb-6">
          In collaboration with <a
            href="https://www.neorni.be" target="_blank"
            class="font-medium text-golden-grass-300 hover:text-golden-grass-200 hover:underline">Neorni</a>,
          we've validated the system in real field conditions and are now transitioning it to its hardened,
          production-grade version. The foundation is proven. The next step is refining it into a mature, long-term
          platform built for scale and reliability.
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <FeatureCard
              icon="i-lucide-check-circle"
              title="What is Proven"
              description="Hardware reliability, solar endurance, cloud infrastructure, compliance logging, and a validated subscription model."
          />
          <FeatureCard
              icon="i-lucide-hammer"
              title="What We Are Building"
              description="Production-grade features including OTA updates, role-based access control, and detailed audit trails for large-scale operation."
          />
          <FeatureCard
              icon="i-lucide-calendar-clock"
              title="Timeline"
              description="Production version targeted for early 2026, followed by a year-long validation period leading to a 2027 rollout."
              bg-color="bg-golden-grass/5"
          />
        </div>

        <p class="text-lg mb-6">
          Looking further ahead, we plan to extend the platform beyond dispensing control. From 2028 onward, we aim to
          integrate optional camera modules to capture feeding events and add the ability to log on-site
          interventions.
          These features will help create a complete historical record of every client location.
        </p>

        <p class="text-lg">
          We are inviting <span class="font-semibold text-golden-grass-300">2 to 3 veterinary practices</span> to join
          us in this next stage of development. Partners will help validate new features, test in the field, and guide
          usability improvements based on real deployments.
        </p>
      </div>
    </UPageSection>

    <UPageSection id="partnership">
      <div class="border border-white/5 rounded-2xl backdrop-blur-lg shadow-2xl px-8 py-15">
        <SectionTitle
            eyebrow="Partnership model"
            title="Bring Statera to your region"
            description="For practices wanting to offer a service like Vets For City Pigeons"
        />

        <p class="text-lg mb-6">
          We are looking for veterinary practices interested in expanding automated dispensing services in their
          regions, and our goal is to work with partners who see the potential of connected infrastructure in daily
          operations. Statera provides a proven foundation that can be deployed and scaled across multiple locations.
        </p>

        <p class="text-lg">
          We welcome inquiries from practices already serving municipalities or looking to do so.
          Collaboration opportunities are <span class="font-semibold text-golden-grass-300">limited</span> to ensure
          quality and regional focus.
        </p>

      </div>
    </UPageSection>

    <UPageSection id="contact">

      <div
          class="relative overflow-hidden rounded-2xl border border-golden-grass/25
         bg-gradient-to-r from-black/40 via-black/30 to-transparent
         px-8 py-10 shadow-2xl backdrop-blur-xl"
      >
        <!-- soft glow accents -->
        <div class="pointer-events-none absolute inset-0 opacity-50">
          <div class="absolute -left-10 top-0 h-40 w-40 rounded-full bg-golden-grass/20 blur-3xl"></div>
          <div class="absolute right-0 -bottom-10 h-48 w-48 rounded-full bg-golden-grass/10 blur-3xl"></div>
        </div>

        <div
            class="relative flex flex-col items-center gap-6 text-center md:flex-row md:items-center md:justify-between md:text-left">
          <div>
            <p class="text-sm font-semibold uppercase tracking-[0.25em] text-golden-grass-400">
              Contact
            </p>
            <h2 class="mt-2 text-3xl font-semibold text-marshland-50">
              Interested in working with us?
            </h2>
            <p class="mt-3 text-base max-w-2xl text-marshland-200/90">
              Reach out to <a
                href="mailto:info@collor.be?subject=Statera%20-%20Partnership%20Inquiry"
                class="font-medium text-golden-grass-300 hover:text-golden-grass-200 hover:underline">info@collor.be</a>.
              We will get back to you to discuss your situation and whether Statera is a good fit.
            </p>
          </div>

          <div class="md:flex-shrink-0">
            <CTAButton
                href="mailto:info@collor.be?subject=Statera%20-%20Partnership%20Inquiry"
                icon="i-lucide-mail"
            >
              Contact us at info@collor.be
            </CTAButton>
          </div>
        </div>
      </div>

    </UPageSection>

    <UPageSection v-if="false" id="remonco">
      <div class="border border-white/5 rounded-2xl backdrop-blur-lg shadow-2xl px-8 py-15">

        <div class="text-center mb-10">
          <h2 class="text-4xl font-bold text-golden-grass/80"> Our Solution </h2>
          <p class="my-2 text-lg">Remote Monitor Control</p>
        </div>

        <div id="remonco-remote" class="py-15">
          <SectionHeader
              number="1"
              title="Remote"
              description="Deploy anywhere – we adapt to YOUR infrastructure needs"
          />

          <div class="flex flex-col md:flex-row gap-5 md:items-stretch">
            <FeatureCard
                icon="i-lucide-map-pin"
                title="Deployment Scenarios"
                bg-color="bg-golden-grass/5"
                class="md:flex-shrink-0"
            >
              <span class="font-semibold">Remote locations:</span> No infrastructure needed<br />
              <span class="font-semibold">Existing sites:</span> Integrate seamlessly<br />
            </FeatureCard>

            <div class="flex flex-col gap-4 md:flex-1">
              <FeatureCard icon="i-lucide-globe" title="Network Options">
                <span class="font-semibold">We Provide It:</span> We equip the devices with a suitable connection.
                <br />
                <span class="font-semibold">Or Plug Us In:</span> We connect the devices to your existing
                                                                  infrastructure.
              </FeatureCard>
              <FeatureCard icon="i-lucide-zap" title="Power Options">
                <span class="font-semibold">We Power It:</span> Built-in solar and battery keep everything running
                                                                anywhere.
                <br />
                <span class="font-semibold">Or Tap The Grid:</span> Plug into standard power or PoE and you're
                                                                set.
              </FeatureCard>
            </div>
          </div>
        </div>

        <div class="flex justify-center text-golden-grass">
          <UIcon name="i-lucide-arrow-down" class="w-7 h-7" />
        </div>

        <div id="remonco-monitor" class="py-15">
          <SectionHeader
              number="2"
              title="Monitor"
              description="Get telemetry data – we measure what you need, process it and visualize it."
          />

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FeatureCard
                icon="i-lucide-database"
                title="Telemetry"
                description="Sensor data captured and securely stored for complete historical records."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-trending-up"
                title="Analytics"
                description="Intuitive dashboards and trend analysis reveal optimization opportunities."
                bg-color="bg-golden-grass/5"
            />
            <FeatureCard
                icon="i-lucide-bell-ring"
                title="Alerts"
                description="Intelligent notifications prevent downtime by alerting you when action is needed."
            />
            <FeatureCard
                icon="i-lucide-monitor-smartphone"
                title="Anywhere"
                description="Access your operation from any device, anywhere in the world."
            />
          </div>
        </div>

        <div class="flex justify-center text-golden-grass">
          <UIcon name="i-lucide-arrow-down" class="w-7 h-7" />
        </div>

        <div id="remonco-control" class="py-15">
          <SectionHeader
              number="3"
              title="Control"
              description="Control actuators – tweak operational procedures"
          />

          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureCard
                icon="i-lucide-sliders-horizontal"
                title="Manual Control"
                description="On/Off switches, valve control, motor speed, setpoint adjustments"
            />
            <FeatureCard
                icon="i-lucide-calendar-range"
                title="Scheduled Operations"
                description="Time-based control, recurring schedules"
            />
            <FeatureCard
                icon="i-lucide-lock"
                title="Access Control"
                description="Role-based permissions, audit logs, secure authentication"
            />
          </div>

        </div>
      </div>
    </UPageSection>

  </div>
</template>

<script setup lang="ts">
</script>
